"use client";
import React, { Fragment, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import cn from "classnames";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios";
import TextareaAutosize from "react-textarea-autosize";

import Button from "@/next-src/components/ui/button/Button";
import useWindowDimensions from "@/next-src/hooks/useWindowDimensions";

import comCSS from "./NewsletterForm.module.scss";
import convertHTMLtoJSX from "@/next-src/utils/convertHTMLtoJSX";

const NewsletterForm = ({ className, data }) => {
  const { width: windowWidth } = useWindowDimensions();
  const [isSent, setIsSent] = useState(false);
  const {
    submitButtonMobile,
    buttonSubmitingMobile,
    buttonSuccessMobile,
    email,
    firstname,
    lastname,
    cityOfResidence,
    questionnaire,
    successMsgMobile,
  } = data || {};

  let { submitButton, buttonSubmiting, buttonSuccess, successMsg } = data || {};

  if (windowWidth < 992) {
    submitButton = submitButtonMobile;
    buttonSubmiting = buttonSubmitingMobile;
    buttonSuccess = buttonSuccessMobile;
    successMsg = successMsgMobile;
  }

  const fieldsData = [
    { field: email, key: "email", errMsg: "Email is required" },
    { field: firstname, key: "firstname", errMsg: "First name is required" },
    { field: lastname, key: "lastname", errMsg: "Last name is required" },
    {
      field: cityOfResidence,
      key: "cityOfResidence",
      errMsg: "City of Residence is required",
    },
    ...(questionnaire || []).map((el, i) => ({
      field: el,
      key: `question${i + 1}`,
      errMsg: "Is required",
    })),
  ];

  // Define validation schema using Yup
  const preValidationSchema = {};
  if (!email.hide) {
    let emailValidation = Yup.string().email("Invalid email");
    if (email.isRequired) {
      emailValidation = emailValidation.required("Email is required");
    }
    preValidationSchema.email = emailValidation;
  }

  fieldsData
    .filter((el) => !el.field?.hide && el.key !== "email")
    .forEach((el) => {
      if (el.field?.isRequired) {
        preValidationSchema[el.key] = Yup.string().required(el.errMsg);
      } else {
        preValidationSchema[el.key] = Yup.string();
      }
    });

  const validationSchema = Yup.object().shape(preValidationSchema);

  // Initial form values
  const initialValues = fieldsData
    .filter((el) => !el.field?.hide)
    .reduce((acc, cur) => {
      acc[cur.key] = "";
      return acc;
    }, {});

  const onSubmit = async (values, { resetForm, setSubmitting }) => {
    const questions = questionnaire
      ?.filter((el) => !el.hide)
      ?.map((el, i) => ({
        question: el.placeholder,
        answer: values[`question${i + 1}`],
      }));
    const reqData = {
      ...values,
      url: window.location?.toString() || "",
      questionnaire: questions,
    };
    try {
      const res = await axios({
        method: "POST",
        data: reqData,
        url: "/api/newsletter-subscriptions",
      });
      if (res) {
        setIsSent(true);
        setTimeout(() => {
          setIsSent(false);
        }, 8000);
        resetForm();
      }
    } catch (error) {
      console.log("error", error);
    }
    setSubmitting(false); // Important: This line tells Formik that submission is complete
  };

  const getButton = (isSubmitting) => {
    if (!isSubmitting && !isSent)
      return (
        <Button
          type="submit"
          data={submitButton}
          disabled={isSubmitting}
          className={cn("button is-inverted w-button", comCSS.btn)}
        />
      );
    if (isSubmitting)
      return (
        <Button
          type="submit"
          data={buttonSubmiting}
          disabled={isSubmitting}
          className={cn("button is-inverted w-button", comCSS.btn)}
        />
      );
    if (isSent)
      return (
        <Button
          type="submit"
          data={buttonSuccess}
          className={cn("button is-inverted w-button", comCSS.btn)}
        />
      );
    return null;
  };

  return (
    <div className={cn(comCSS.wrapper, className)}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, values, handleChange, errors, touched }) => (
          <Form className={cn(comCSS.form)}>
            {!firstname.hide && (
              <div>
                <Field
                  className="form-input is-custom  w-input"
                  maxLength="256"
                  name="firstname"
                  placeholder={firstname?.placeholder}
                  type="text"
                  id="first-name-4"
                />

                <ErrorMessage
                  name="firstname"
                  component="div"
                  className={comCSS.error}
                />
              </div>
            )}
            {!lastname.hide && (
              <div>
                <Field
                  className="form-input is-custom  w-input"
                  maxLength="256"
                  name="lastname"
                  placeholder={lastname?.placeholder}
                  type="text"
                  id="last-name-3"
                />

                <ErrorMessage
                  name="lastname"
                  component="div"
                  className={comCSS.error}
                />
              </div>
            )}
            {!cityOfResidence.hide && (
              <div>
                <Field
                  className="form-input is-custom  w-input"
                  maxLength="256"
                  name="cityOfResidence"
                  placeholder={cityOfResidence?.placeholder}
                  id="cityOfResidence"
                />

                <ErrorMessage
                  name="cityOfResidence"
                  component="div"
                  className={comCSS.error}
                />
              </div>
            )}

            {!email.hide && (
              <div>
                <Field
                  className="form-input is-custom  w-input"
                  maxLength="256"
                  name="email"
                  placeholder={email?.placeholder}
                  type="email"
                  id="email-6"
                />

                <ErrorMessage
                  name="email"
                  component="div"
                  className={comCSS.error}
                />
              </div>
            )}

            {questionnaire
              ?.filter((el) => !el.hide)
              ?.map((el, i) => (
                <div key={el.id}>
                  <TextareaAutosize
                    name={`question${i + 1}`}
                    cols="40"
                    minRows={2}
                    value={values[`question${i + 1}`]}
                    onChange={(e) => {
                      handleChange(e);
                    }}
                    placeholder={el.placeholder}
                    className={cn(
                      comCSS.formControlTextarea,
                      "form-input is-custom  w-input"
                    )}
                  />

                  {touched[`question${i + 1}`] &&
                    errors[`question${i + 1}`] && (
                      <div className={comCSS.error}>
                        {errors[`question${i + 1}`]}
                      </div>
                    )}
                </div>
              ))}
            <div className={comCSS.btnWrapper}>
              {getButton(isSubmitting, isSent)}
            </div>
          </Form>
        )}
      </Formik>
      {isSent && successMsg ? (
        <div className={cn(comCSS.successMsg)}>
          {convertHTMLtoJSX(successMsg)}
        </div>
      ) : null}
    </div>
  );
};

const FormFieldType = PropTypes.shape({
  placeholder: PropTypes.string, // First name placeholder (optional)
  isRequired: PropTypes.bool, // Indicates if first name is required (optional)
  hide: PropTypes.bool, // Indicates if first name field is hidden (optional)
  label: PropTypes.string, // First name label (optional)
});

NewsletterForm.propTypes = {
  className: PropTypes.string,
  data: PropTypes.shape({
    mode: PropTypes.string, // Form mode (optional)
    firstname: FormFieldType,
    lastname: FormFieldType,
    email: FormFieldType,
    cityOfResidence: FormFieldType,
    questionnaire: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string, // Questionnaire item ID (optional)
        placeholder: PropTypes.string, // Questionnaire item placeholder (optional)
        isRequired: PropTypes.bool, // Indicates if questionnaire item is required (optional)
        hide: PropTypes.bool, // Indicates if questionnaire item is hidden (optional)
        label: PropTypes.string, // Questionnaire item label (optional)
      })
    ),
    successMsg: PropTypes.string, // Success message (optional)
    successMsgMobile: PropTypes.string, // Mobile success message (optional)
    submitButton: PropTypes.shape({
      id: PropTypes.string, // Submit button ID (optional)
      text: PropTypes.string.isRequired, // Submit button text (consider removing HTML tags)
      url: PropTypes.string, // Submit button URL (optional)
      bgColor: PropTypes.string, // Submit button background color (optional)
      bgColorHover: PropTypes.string, // Submit button hover background color (optional)
      borderColor: PropTypes.string, // Submit button border color (optional)
      borderColorHover: PropTypes.string, // Submit button hover border color (optional)
      hide: PropTypes.bool, // Indicates if submit button is hidden (optional)
      action: PropTypes.string, // Submit button action (optional)
      calendlyLink: PropTypes.string, // Calendly link (optional)
    }),
    submitButtonMobile: PropTypes.shape({
      // Similar structure to submitButton
      id: PropTypes.string, // Submit button ID (optional)
      text: PropTypes.string.isRequired, // Submit button text (consider removing HTML tags)
      url: PropTypes.string, // Submit button URL (optional)
      bgColor: PropTypes.string, // Submit button background color (optional)
      bgColorHover: PropTypes.string, // Submit button hover background color (optional)
      borderColor: PropTypes.string, // Submit button border color (optional)
      borderColorHover: PropTypes.string, // Submit button hover border color (optional)
      hide: PropTypes.bool, // Indicates if submit button is hidden (optional)
      action: PropTypes.string, // Submit button action (optional)
      calendlyLink: PropTypes.string, // Calendly link (optional)
    }),
    buttonSubmiting: PropTypes.shape({
      // Similar structure to submitButton
      id: PropTypes.string, // Submit button ID (optional)
      text: PropTypes.string.isRequired, // Submit button text (consider removing HTML tags)
      url: PropTypes.string, // Submit button URL (optional)
      bgColor: PropTypes.string, // Submit button background color (optional)
      bgColorHover: PropTypes.string, // Submit button hover background color (optional)
      borderColor: PropTypes.string, // Submit button border color (optional)
      borderColorHover: PropTypes.string, // Submit button hover border color (optional)
      hide: PropTypes.bool, // Indicates if submit button is hidden (optional)
      action: PropTypes.string, // Submit button action (optional)
      calendlyLink: PropTypes.string, // Calendly link (optional)
    }),
    buttonSubmitingMobile: PropTypes.shape({
      // Similar structure to submitButton
      id: PropTypes.string, // Submit button ID (optional)
      text: PropTypes.string.isRequired, // Submit button text (consider removing HTML tags)
      url: PropTypes.string, // Submit button URL (optional)
      bgColor: PropTypes.string, // Submit button background color (optional)
      bgColorHover: PropTypes.string, // Submit button hover background color (optional)
      borderColor: PropTypes.string, // Submit button border color (optional)
      borderColorHover: PropTypes.string, // Submit button hover border color (optional)
      hide: PropTypes.bool, // Indicates if submit button is hidden (optional)
      action: PropTypes.string, // Submit button action (optional)
      calendlyLink: PropTypes.string, // Calendly link (optional)
    }),
    buttonSuccess: PropTypes.shape({
      // Similar structure to submitButton
      id: PropTypes.string, // Submit button ID (optional)
      text: PropTypes.string.isRequired, // Submit button text (consider removing HTML tags)
      url: PropTypes.string, // Submit button URL (optional)
      bgColor: PropTypes.string, // Submit button background color (optional)
      bgColorHover: PropTypes.string, // Submit button hover background color (optional)
      borderColor: PropTypes.string, // Submit button border color (optional)
      borderColorHover: PropTypes.string, // Submit button hover border color (optional)
      hide: PropTypes.bool, // Indicates if submit button is hidden (optional)
      action: PropTypes.string, // Submit button action (optional)
      calendlyLink: PropTypes.string, // Calendly link (optional)
    }),
    buttonSuccessMobile: PropTypes.shape({
      // Similar structure to submitButton
      id: PropTypes.string, // Submit button ID (optional)
      text: PropTypes.string.isRequired, // Submit button text (consider removing HTML tags)
      url: PropTypes.string, // Submit button URL (optional)
      bgColor: PropTypes.string, // Submit button background color (optional)
      bgColorHover: PropTypes.string, // Submit button hover background color (optional)
      borderColor: PropTypes.string, // Submit button border color (optional)
      borderColorHover: PropTypes.string, // Submit button hover border color (optional)
      hide: PropTypes.bool, // Indicates if submit button is hidden (optional)
      action: PropTypes.string, // Submit button action (optional)
      calendlyLink: PropTypes.string, // Calendly link (optional)
    }),
  }).isRequired,
};

export default NewsletterForm;
